/**
 * MergesTab — AdminReview "Merges" sub-tab (Phase 1.14.3 §6.3).
 *
 * Lists in-flight merge drafts in the current tenant. Operators click
 * into a draft to review + apply it. The list is the collaborative
 * pickup-point for drafts started on OrganizationDetail via the
 * "Mark as duplicate of…" affordance.
 *
 * Phase 1.14.3.1 brand polish — sage / cream palette, Inter body,
 * softer microcopy.
 *
 * Page-level gate: this tab is hidden when the user lacks
 * merge_providers (AdminReviewTabs hides it). The server's GET
 * /providers/merges endpoint also 403s on missing capability, so the
 * data fetch will fail loud if a non-grantee somehow navigates here.
 */

import { type ReactNode } from 'react';
import { Merge } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Skeleton } from '@/components/Skeleton';
import { ErrorBanner } from '@/components/ErrorBanner';

export interface MergesTabProps {
  tenantSlug: string;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const ms = Date.now() - then;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function MergesTab({ tenantSlug }: MergesTabProps): ReactNode {
  const query = useQuery({
    queryKey: ['provider-merges', tenantSlug],
    queryFn: () => api.listProviderMerges(tenantSlug),
    enabled: Boolean(tenantSlug),
  });

  if (query.isPending) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="the merges" />;
  }

  const merges = query.data?.merges ?? [];

  if (merges.length === 0) {
    return (
      <div className="rounded-card border border-sage-200 bg-sand p-5 text-sm">
        <div className="flex items-center gap-2 text-sage-700">
          <Merge size={15} aria-hidden="true" className="shrink-0" />
          <span className="font-semibold">No merges in progress</span>
        </div>
        <p className="mt-1.5 text-ink-soft">
          Drafts started via &ldquo;Mark as duplicate of…&rdquo; on an
          organization page show up here until they&apos;re applied or
          discarded. Two stewards working in parallel can pick up each
          other&apos;s drafts from this list.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-card border border-sage-800/[8%] bg-paper shadow-sh1">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-sage-200 bg-sage-50 text-xs uppercase tracking-wider text-sage-700">
          <tr>
            <th className="px-4 py-3 font-semibold">Records</th>
            <th className="px-4 py-3 font-semibold">Started</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {merges.map((m) => (
            <tr
              key={m.id}
              className="border-b border-sage-100 last:border-0 hover:bg-sage-50/50"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-ink-soft">{m.loser_name}</span>
                  <span aria-hidden="true" className="text-sage-400">
                    →
                  </span>
                  <span className="font-medium text-sage-800">
                    {m.winner_name}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-ink-soft">
                {timeAgo(m.proposed_at)}
              </td>
              <td className="px-4 py-3 text-right">
                <Link
                  to={`/t/${encodeURIComponent(tenantSlug)}/merges/${encodeURIComponent(m.id)}`}
                  className="inline-flex items-center gap-1 font-semibold text-sage-700 hover:text-sage-900 hover:underline underline-offset-2"
                >
                  Review
                  <span aria-hidden="true">→</span>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
